import { ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild, signal, ViewEncapsulation } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { CompanyService } from '../../services/company.service';
import { AuthenticationService } from '../../services/authentication.service';
import { CRMIntegrationsService } from '../../services/crm-integrations.service';
import { CommunicationService } from '../../services/communication.service';
import { TaggingService } from '../../services/tagging.service';
import { publicMethods } from '../../globals';
import { DashboardBulkSelection } from '../../types/dashboard-bulk-selection';
import { buildTradeTimeline, TradeTimeline } from './trade-timeline';
import { buildInsightCategoryChart } from '../company-profile-template/insight-category-chart';
import { CompanyProfileInsight, hasInsightValue, insightDetailRows } from '../company-profile-template/profile-insight';
import { companyProfileOfferingItems } from '../company-profile-template/company-profile-offerings.component';
import { assetBreakdownRows, financialRow, financialSections, FinancialFigure } from '../company-profile-template/financial-report';
import { LOGISTICS_WEB_TECHNOLOGY_CATEGORIES, selectOptionsByValue } from '../dashboard/logistics-dashboard/logistics-dashboard.config';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as shape from 'd3-shape';
import { GraphComponent, NgxGraphStateChangeEvent, NgxGraphStates } from '@swimlane/ngx-graph';

declare var $: any;

@Component({
  selector: 'logistics-company-profile',
  templateUrl: './logistics-company-profile.component.html',
  styleUrl: './logistics-company-profile.component.css',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class LogisticsCompanyProfileComponent {
  companyNumber = '';
  company: Record<string, any> = {};
  loading = true;
  loadingError = '';
  jobsListToRender: Record<string, any>[] = [];
  showAllKeyPeople = false;
  thirdPartyNewsSearchText = '';
  selfPublishedCompanyNewsSearchText = '';
  logisticsNewsFilter: 'all' | 'news' | 'self_published_news' = 'all';
  logisticsNewsQuery = '';
  logisticsNewsVisibleCount = 5;
  isSmallScreen = signal(window.innerWidth <= 700);
  userCustomProperties: Record<string, any> = {};
  workspaceFieldsLoading = true;
  workspaceFieldsUpdated = false;
  workspaceFieldQuery = '';
  accountDetails: { email?: string; isSuperUser?: boolean } = {};
  teamMembers: Record<string, any>[] = [];
  activeCRMList: Record<string, any>[] = [];
  crmExporterName = '';
  companyActionSelection: DashboardBulkSelection = { mode: 'none' };
  readonly logisticsChartColours = ['#244a3b', '#4f7853', '#78984c', '#a5bd62', '#cedb8a', '#7a9f85', '#dbe9ac'];
  logisticsWebsiteTechnologies: { name: string; category: string }[] = [];
  private tradeTimelineSource: any;
  private tradeTimelineCache: TradeTimeline;
  activeProfileModal: 'financials' | 'charges' | 'trade' | 'corporate' | null = null;
  private profileModalTrigger: HTMLElement | null = null;
  @ViewChild('profileModalClose') profileModalClose: ElementRef<HTMLButtonElement>;
  financialTab = 'Profit and Loss Account';
  detailedTrade: Record<string, any> = {};
  detailedTradeLoading = false;
  corporateStructure: Record<string, any> = {};
  private initialCorporateStructure: Record<string, any> = {};
  readonly corporateTreeCurve = shape.curveLinear;
  readonly corporateTreeLayoutSettings = { orientation: 'TB' };
  @ViewChild('corporateTreeGraph') corporateTreeGraph: GraphComponent;
  private corporateTreeNeedsFit = false;
  focusedCorporateNodeId = '';
  corporateGraphLoading = false;
  corporateGraphError = '';
  corporateExpansionLoading = '';
  corporateExpansionError = '';
  connectedDirectorships: Record<string, any> = {};
  private corporateGraphRequested = false;
  private relatedGraphRequested = false;

  loadCorporateGraph(event: Event): void {
    if (!(event.target as HTMLDetailsElement).open || this.corporateGraphRequested) return;
    this.corporateGraphRequested = true;
    this.corporateGraphLoading = true;
    this.corporateGraphError = '';
    this.companyService.getCorporateStructureGraph(this.companyNumber).subscribe({
      next: data => {
        this.corporateGraphLoading = false;
        if (!Array.isArray(data?.corporate_structure_graph?.nodes)) {
          this.corporateGraphError = 'The corporate tree could not be loaded.';
          this.corporateGraphRequested = false;
          return;
        }
        this.corporateStructure = this.copyCorporateGraph(data.corporate_structure_graph);
        this.initialCorporateStructure = this.copyCorporateGraph(data.corporate_structure_graph);
      },
      error: () => {
        this.corporateGraphLoading = false;
        this.corporateGraphError = 'The corporate tree could not be loaded.';
        this.corporateGraphRequested = false;
      },
    });
  }
  private copyCorporateGraph(graph: Record<string, any>): Record<string, any> {
    return {
      nodes: (graph?.nodes || []).map((node: any) => ({ ...node })),
      links: (graph?.links || []).map((link: any) => ({ ...link })),
    };
  }
  expandCorporateTree(nodeId: string, direction: 'ancestors' | 'descendants'): void {
    if (this.corporateExpansionLoading) return;
    this.corporateExpansionLoading = `${nodeId}:${direction}`;
    this.corporateExpansionError = '';
    this.companyService.getExpandedCorporateStructure(String(nodeId), direction).subscribe({
      next: data => {
        this.corporateExpansionLoading = '';
        const incoming = data?.corporate_structure_graph;
        if (!Array.isArray(incoming?.nodes) || !Array.isArray(incoming?.links)) {
          this.corporateExpansionError = 'Could not expand this part of the tree. Please try again.';
          return;
        }
        const nodes = this.corporateStructure.nodes || [];
        const links = this.corporateStructure.links || [];
        const nodeIds = new Set(nodes.map((node: any) => String(node.id)));
        const linkKey = (link: any) => String(link.id || `${link.source}:${link.target}:${link.label || ''}`);
        const linkIds = new Set(links.map(linkKey));
        this.corporateStructure = {
          nodes: [...nodes, ...incoming.nodes.filter((node: any) => !nodeIds.has(String(node.id)))],
          links: [...links, ...incoming.links.filter((link: any) => !linkIds.has(linkKey(link)))],
        };
        this.corporateTreeNeedsFit = true;
      },
      error: () => {
        this.corporateExpansionLoading = '';
        this.corporateExpansionError = 'Could not expand this part of the tree. Please try again.';
      },
    });
  }
  focusCorporateTree(nodeId: string): void {
    const node = (this.corporateStructure.nodes || []).find((item: any) => String(item.id) === String(nodeId));
    if (!node) return;
    this.focusedCorporateNodeId = String(node.id);
    this.corporateTreeGraph?.panToNodeId(node.id);
    this.corporateExpansionError = '';
  }
  resetCorporateTree(): void {
    this.corporateTreeNeedsFit = true;
    this.focusedCorporateNodeId = '';
    this.corporateStructure = this.copyCorporateGraph(this.initialCorporateStructure);
    this.corporateExpansionError = '';
  }
  onCorporateTreeStateChange(event: NgxGraphStateChangeEvent): void {
    if (event.state !== NgxGraphStates.Output || !this.corporateTreeNeedsFit || !this.corporateTreeGraph) return;
    this.corporateTreeNeedsFit = false;
    this.corporateTreeGraph.zoomToFit({ autoCenter: true, force: true });
  }
  loadRelatedGraph(event: Event): void {
    if (!(event.target as HTMLDetailsElement).open || this.relatedGraphRequested) return;
    this.relatedGraphRequested = true;
    this.companyService.getRelatedPeopleGraph(this.companyNumber).subscribe({
      next: data => this.connectedDirectorships = data?.related_people_graph || {},
      error: () => this.relatedGraphRequested = false,
    });
  }

  get logisticsSicCodes(): Record<string, any>[] { return this.company?.company_overview?.sic_codes || []; }
  get logisticsFinancialSections() { return financialSections(this.company?.financials?.accounts); }
  get hasLogisticsFinancialReport() { return this.logisticsFinancialSections.some(section => section.rows.length > 0) || this.logisticsAssetReportRows.length > 0; }
  get logisticsPrimaryFigure() {
    for (const name of ['Revenue', 'Cash At Bank', 'Current Assets']) {
      const row = financialRow(this.logisticsFinancialSections, name);
      if (row) return row;
    }
    return null;
  }
  get logisticsAssetFigures() {
    const names = ['Inventory', 'Vehicles', 'Tangible Assets', 'Property, Plant and Equipment'];
    const figures = names.map(name => financialRow(this.logisticsFinancialSections, name)).filter(Boolean);
    if (!figures.some(figure => figure.name === 'Vehicles')) {
      const vehicle = this.logisticsAssetReportRows.find(item => item.name === 'tangible · vehicles');
      if (vehicle) figures.push({ ...vehicle, name: 'Vehicles' });
    }
    return figures;
  }
  get logisticsAssetReportRows(): FinancialFigure[] { return assetBreakdownRows(this.company?.asset_breakdown); }
  get logisticsShareholders(): Record<string, any>[] {
    return this.company?.confirmation_statements?.[0]?.shareholders_chart_data || [];
  }
  get logisticsCorporateNodes(): Record<string, any>[] {
    return (this.corporateStructure?.nodes || []).filter((node: any) => node.label);
  }
  logisticsCorporateRelationshipText(label: string): string {
    return (label || '').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '').trim()
      || 'Ownership or control relationship; further details are unavailable.';
  }
  get logisticsCorporateLinks(): Array<{ parent: any; child: any; label: string }> {
    const nodes = new Map(this.logisticsCorporateNodes.map(node => [String(node.id), node]));
    return (this.corporateStructure?.links || []).map((link: any) => ({
      parent: nodes.get(String(link.source)), child: nodes.get(String(link.target)), label: link.label || 'Controls',
    })).filter((link: any) => link.parent && link.child);
  }
  get logisticsConnectedCompanies(): Record<string, any>[] {
    const seen = new Set<string>();
    const nodes = this.connectedDirectorships?.nodes || [];
    const people = new Map(nodes.filter((node: any) => node.node_type === 'person').map((node: any) => [String(node.id), node.label]));
    return nodes.filter((node: any) => {
      if (node.node_type !== 'business' || !node.label || node.label === this.company?.company_overview?.name || seen.has(node.label)) return false;
      seen.add(node.label);
      return true;
    }).map((node: any) => ({ ...node, sharedPeople: (this.connectedDirectorships?.links || [])
      .filter((link: any) => String(link.target) === String(node.id))
      .map((link: any) => `${people.get(String(link.source)) || 'Shared leader'}${link.label ? ` · ${link.label}` : ''}`) }));
  }
  logisticsCompanyNumberFromLink(link: unknown): string {
    const match = String(link || '').match(/\/profile\/([^/?#]+)/);
    return match?.[1] || '';
  }
  logisticsTradeGroups(kind: 'detailed_imports' | 'detailed_exports'): any[] {
    return Array.isArray(this.detailedTrade?.[kind]) ? this.detailedTrade[kind] : [];
  }
  get logisticsShareholderTotal(): number {
    return this.logisticsShareholders.reduce((sum, row) => sum + (Number(row.shares_held) || 0), 0);
  }
  get logisticsShareholderGradient(): string {
    const palette = ['#244a3b', '#5d8458', '#a4bc6c', '#d0dba4', '#8ca99d', '#638d72'];
    const total = this.logisticsShareholderTotal;
    if (!total) return '#e7eee4';
    let cursor = 0;
    return `conic-gradient(${this.logisticsShareholders.map((row, index) => {
      const start = cursor;
      cursor += (Number(row.shares_held) || 0) / total * 100;
      return `${palette[index % palette.length]} ${start}% ${cursor}%`;
    }).join(', ')})`;
  }
  logisticsShareholderColour(index: number): string { return ['#244a3b', '#5d8458', '#a4bc6c', '#d0dba4', '#8ca99d', '#638d72'][index % 6]; }
  logisticsFlagCode(value: unknown): string {
    const code = Array.isArray(value) ? value[0] : value;
    return typeof code === 'string' ? code.toLowerCase() : '';
  }
  logisticsFormattedFigure(value: unknown, type = 'funds'): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value !== 'number' && !Number.isFinite(Number(value))) return String(value);
    const number = Number(value);
    return `${type === 'funds' ? '£' : ''}${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(number)}`;
  }
  openProfileModal(modal: 'financials' | 'charges' | 'trade' | 'corporate'): void {
    this.profileModalTrigger = document.activeElement as HTMLElement;
    this.corporateTreeNeedsFit = modal === 'corporate';
    this.activeProfileModal = modal;
    if (modal === 'financials' && !this.logisticsFinancialSections.some(section => section.name === this.financialTab && section.rows.length)) {
      this.financialTab = this.logisticsFinancialSections.find(section => section.rows.length)?.name || (this.logisticsAssetReportRows.length ? 'Asset Breakdown' : 'Profit and Loss Account');
    }
    setTimeout(() => this.profileModalClose?.nativeElement.focus());
    if (modal === 'trade' && !Object.keys(this.detailedTrade).length && !this.detailedTradeLoading) {
      this.detailedTradeLoading = true;
      this.companyService.getCompanyDetailedImportsExports(this.companyNumber).subscribe({
        next: data => { this.detailedTrade = data || {}; this.detailedTradeLoading = false; },
        error: () => { this.detailedTradeLoading = false; },
      });
    }
  }
  closeProfileModal(): void {
    this.activeProfileModal = null;
    setTimeout(() => this.profileModalTrigger?.focus());
  }
  logisticsTradeDescription(item: any, full = false): string {
    const raw = item?.description || item?.commodity_description || item?.item_description || item?.product_description || item?.goods_description || '';
    const parts = String(raw).replace(/<br\s*\/?\s*>/gi, '\n').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').split('\n').map(part => part.replace(/\s+/g, ' ').trim()).filter(Boolean);
    return (full ? parts : parts.slice(-2)).join(' · ');
  }

  get logisticsNewsArticles(): Record<string, any>[] {
    const latest = this.company?.latest_news || {};
    const articles = [
      ...(latest.news || []).map(article => ({ ...article, kind: 'news', date: this.logisticsNewsDate(article.published_date), link: article.article_link })),
      ...(latest.self_published_news || []).map(article => ({ ...article, kind: 'self_published_news', date: this.logisticsNewsDate(article.article_published_date), link: article.url, title: article.article_title || article.title })),
    ];
    const query = this.logisticsNewsQuery.trim().toLocaleLowerCase();
    return articles
      .filter(article => this.logisticsNewsFilter === 'all' || article.kind === this.logisticsNewsFilter)
      .filter(article => !query || [article.title, article.source, article.description, article.article_description]
        .some(value => String(value || '').toLocaleLowerCase().includes(query)))
      .sort((a, b) => (Date.parse(b.date || '') || 0) - (Date.parse(a.date || '') || 0));
  }

  logisticsNewsCount(kind: 'news' | 'self_published_news'): number {
    return this.company?.latest_news?.[kind]?.length || 0;
  }

  logisticsNewsDate(value: unknown): string | null {
    if (!value || !Number.isFinite(Date.parse(String(value)))) return null;
    return new Date(String(value)).toISOString();
  }

  logisticsNewsSummary(article: Record<string, any>): string {
    const raw = String(article.description || article.article_description || '');
    const plain = raw.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/\s+/g, ' ').trim();
    if (/^The post .+ appeared first on /i.test(plain) || plain === article.title) return '';
    return plain;
  }

  selectLogisticsNewsFilter(filter: 'all' | 'news' | 'self_published_news'): void {
    this.logisticsNewsFilter = filter;
    this.logisticsNewsVisibleCount = 5;
  }

  logisticsAddressWithCommas(address: string): string {
    // Company profile addresses arrive as newline-separated address fields.
    // Keep each field intact so street names and company names are not guessed from spaces.
    return String(address || '').split(/\r?\n/).map(part => part.trim().replace(/,\s*$/, '')).filter(Boolean).join(', ');
  }

  logisticsPersonName(person: Record<string, any>): string {
    return [person?.forename, person?.middle_name, person?.surname]
      .filter(Boolean).join(' ').trim() || 'Name unavailable';
  }

  logisticsPersonLinkedInUrl(person: Record<string, any>): string | null {
    const rawUrl = typeof person?.linkedin_url === 'string' ? person.linkedin_url.trim() : '';
    if (rawUrl) {
      try {
        const url = new URL(rawUrl);
        if (['http:', 'https:'].includes(url.protocol) &&
            (url.hostname === 'linkedin.com' || url.hostname.endsWith('.linkedin.com'))) {
          url.protocol = 'https:';
          return url.toString();
        }
      } catch { /* Fall through to a person search. */ }
    }

    if (!person?.forename || !person?.surname) return null;
    const keywords = [person.forename, person.surname, this.company.company_overview?.name]
      .filter(Boolean).join(' ');
    return `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(keywords)}`;
  }

  logisticsPersonLinkedInIsDirect(person: Record<string, any>): boolean {
    if (!person?.is_direct_linkedin_url) return false;
    const url = this.logisticsPersonLinkedInUrl(person);
    return !!url && !url.includes('/search/results/people/');
  }

  logisticsPersonNationalities(person: Record<string, any>): string {
    const names = Array.isArray(person?.nationalities) ? person.nationalities : [];
    const codes = Array.isArray(person?.nationality_codes) ? person.nationality_codes : [];
    return (names.length ? names : codes).filter(Boolean).join(', ');
  }

  get logisticsFaviconDomain(): string {
    const website = this.company?.company_overview?.domain || this.company?.company_overview?.url;
    if (!website) return '';
    try {
      return new URL(website.includes('://') ? website : `https://${website}`).hostname;
    } catch (_) {
      return '';
    }
  }

  get logisticsHeatTooltip(): string {
    const events = Number(this.userCustomProperties?.heat_score?.number_of_events) || 0;
    const users = Number(this.userCustomProperties?.heat_score?.number_of_users) || 0;
    if (!events) return 'No team activity in the last 30 days';
    return `${events} ${events === 1 ? 'activity' : 'activities'} by ${users} ${users === 1 ? 'person' : 'people'} in the last 30 days`;
  }

  private selectLogisticsWebsiteTechnologies(groupedTechnologies: Record<string, any[]> | null | undefined): { name: string; category: string }[] {
    const seenNames = new Set<string>();
    return selectOptionsByValue(Object.entries(groupedTechnologies || {}), LOGISTICS_WEB_TECHNOLOGY_CATEGORIES, ([category]) => category)
      .filter(([, technologies]) => Array.isArray(technologies))
      .flatMap(([category, technologies]) => technologies.map(technology => ({ name: String(technology?.name || '').trim(), category })))
      .filter(technology => {
        const key = technology.name.toLocaleLowerCase();
        if (!key || seenNames.has(key)) return false;
        seenNames.add(key);
        return true;
      });
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private companyService: CompanyService,
    private crmService: CRMIntegrationsService,
    private communicationService: CommunicationService,
    private taggingService: TaggingService,
    private authenticationService: AuthenticationService,
    private titleService: Title,
    private cdr: ChangeDetectorRef,
    public pubMethods: publicMethods,
  ) {}

  @HostListener('window:resize')
  onResize(): void {
    this.isSmallScreen.set(window.innerWidth <= 700);
  }

  ngOnInit(): void {
    this.companyNumber = this.route.snapshot.paramMap.get('id') || '';
    this.communicationService.getEmailMessage().subscribe(email => this.accountDetails.email = email);
    this.communicationService.getIsSuperUserMessage().subscribe(isSuperUser => this.accountDetails.isSuperUser = isSuperUser);
    try {
      const saved = window.localStorage.getItem('zint-logistics-results-perspective');
      if (this.logisticsLenses.some(lens => lens.id === saved)) this.logisticsLens = saved;
    } catch (_) {}
    this.loadLogisticsProfileInsights();
    this.loadWorkspaceFields();
    this.companyService.getCompanyProfileData(this.companyNumber).subscribe({
      next: data => {
        this.company = data?.company || {};
        this.logisticsWebsiteTechnologies = this.selectLogisticsWebsiteTechnologies(this.company.website?.web_technologies);
        const companyId = Number(this.company.company_id);
        this.companyActionSelection = Number.isSafeInteger(companyId) && companyId > 0
          ? { mode: 'ids', ids: [companyId], searchParams: new HttpParams() }
          : { mode: 'none' };
        this.jobsListToRender = this.company.hiring_for || [];
        this.titleService.setTitle(`${this.company.company_overview?.name || 'Company'} | Zint Logistics`);
        this.loading = false;
      },
      error: error => {
        this.loadingError = error?.error?.message || 'Company profile could not be loaded.';
        this.loading = false;
      },
    });
    this.crmService.getAvailableCRMs().pipe(
      switchMap(data => {
        const enabled = (Array.isArray(data) ? data : []).filter(crm => crm.functionalityEnabled);
        if (!enabled.length) return of([]);
        return forkJoin(enabled.map(crm => this.crmService.getConfigsForCRM(crm.crmType).pipe(
          map(workflow => {
            const steps = Array.isArray(workflow?.steps) ? workflow.steps : [];
            const connected = steps.some(step => step.stepType === 'auth' && step.isReady === true);
            const configured = steps.some(step => step.stepType === 'object_type' && step.isReady === true);
            return connected && configured ? crm : null;
          }),
          catchError(() => of(null)),
        )));
      }),
    ).subscribe(active => {
      this.activeCRMList = active.filter(Boolean);
    });
  }

  loadWorkspaceFields(): void {
    this.workspaceFieldsLoading = true;
    this.companyService.getCompanyUserDetails(this.companyNumber).subscribe({
      next: data => {
        this.userCustomProperties = data || {};
        this.teamMembers = this.userCustomProperties.team_members || [];
        (this.userCustomProperties.custom_columns || []).forEach(column => {
          if (column.column_type === 'User') column.options = this.teamMembers;
        });
        this.workspaceFieldsLoading = false;
      },
      error: () => { this.workspaceFieldsLoading = false; },
    });
  }

  get logisticsWorkspaceColumns(): Record<string, any>[] {
    return (this.userCustomProperties.custom_columns || []).filter(column =>
      !column.is_propensity || (column.value !== null && column.value !== undefined && column.value !== '')
    );
  }

  get logisticsWorkspaceFilledCount(): number {
    return this.logisticsWorkspaceColumns.filter(column =>
      column.value !== null && column.value !== undefined && column.value !== ''
    ).length;
  }

  workspaceFieldMatches(column: Record<string, any>): boolean {
    return String(column.name || '').toLocaleLowerCase().includes(this.workspaceFieldQuery.trim().toLocaleLowerCase());
  }

  get logisticsWorkspaceMatchingCount(): number {
    return this.logisticsWorkspaceColumns.filter(column => this.workspaceFieldMatches(column)).length;
  }

  get logisticsWorkspaceTags(): Record<string, any>[] {
    return [
      ...(this.userCustomProperties.tags || []).map(tag => ({ ...tag, can_remove: true })),
      ...(this.userCustomProperties.team_member_tags || []).map(tag => ({ ...tag, can_remove: false })),
      ...(this.userCustomProperties.organisation_tags || []).map(tag => ({ ...tag, can_remove: !!tag.can_edit })),
    ].filter(tag => !tag.is_propensity);
  }

  removeWorkspaceTag(tag: Record<string, any>): void {
    if (!tag.can_remove) return;
    this.taggingService.tagOrUntagCompanies(tag.tag_id, [this.company.company_id], null, false).subscribe({
      next: data => {
        if (data?.total === 1) this.loadWorkspaceFields();
        else this.pubMethods.showInfoMessage('Could not remove tag.');
      },
      error: () => this.pubMethods.showInfoMessage('Could not remove tag.'),
    });
  }

  onWorkspaceFieldSaved(value: any): void {
    this.workspaceFieldsUpdated = true;
    this.loadWorkspaceFields();
  }

  get hasLogisticsHeatScore(): boolean {
    return this.pubMethods.hasData(this.userCustomProperties?.heat_score);
  }

  openLogisticsCompanyAction(action: 'Tag' | 'CRM', crmName = ''): void {
    if (this.companyActionSelection.mode === 'none') return;
    if (action === 'CRM') {
      this.crmExporterName = crmName;
      this.cdr.detectChanges();
    }
    $(action === 'Tag' ? '#tagModal' : '#exportToCRMModal').modal('show');
  }

  logisticsCRMIcon(crmType: string): string {
    if (crmType === 'hubspot' || crmType === 'salesforce') {
      return `<i class="fab fa-${crmType}"></i>`;
    }
    const svgPath = `/assets/tech-icons/${crmType}.svg`;
    return `<img src="${svgPath}" alt="${crmType.charAt(0).toUpperCase() + crmType.slice(1)}">`;
  }

  logisticsInsightModules: any[] = [];
  logisticsInsightsLoading = false;
  logisticsInsightsError = false;
  readonly logisticsInitialCardCount = 6;
  showAllLogisticsInsights = false;

  // Shown by company-profile-offerings, which strips segment prefixes and removes duplicates.
  get logisticsProductsAndServices(): string[] {
    return companyProfileOfferingItems(this.company?.company_overview);
  }

  selectedLogisticsInsight: CompanyProfileInsight | null = null;
  selectedLogisticsInsightRows: Array<{ label: string; value: string }> = [];

  // The shared evidence dialog moves focus into itself and restores it to the card's button when it closes.
  openLogisticsEvidence(insight: CompanyProfileInsight): void {
    this.selectedLogisticsInsightRows = insightDetailRows(insight.value, insight.title);
    this.selectedLogisticsInsight = insight;
  }

  closeLogisticsEvidence(): void {
    this.selectedLogisticsInsight = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.activeProfileModal) this.closeProfileModal();
  }
  logisticsLens = 'overview';
  readonly logisticsLenses = [
    { id: 'overview', label: 'Overview', icon: 'fa-grid-2', description: 'The whole opportunity' },
    { id: 'freight-forwarding', label: 'Forwarding', icon: 'fa-truck-fast', description: 'Freight and flows' },
    { id: 'third-party-logistics', label: '3PL', icon: 'fa-warehouse', description: 'Storage and handling' },
    { id: 'parcel-courier', label: 'Courier', icon: 'fa-box-taped', description: 'Parcel demand' },
    { id: 'customs-trade', label: 'Customs', icon: 'fa-globe-europe', description: 'Cross-border trade' },
  ];
  setLogisticsLens(id: string): void {
    this.logisticsLens = id;
    this.showAllLogisticsInsights = false;
    try { window.localStorage.setItem('zint-logistics-results-perspective', id); } catch (_) {}
  }

  private static readonly sectionEyebrows: Record<string, string> = {
    warehouse: 'WAREHOUSING', international: 'CROSS-BORDER', freight: 'FREIGHT', parcel: 'PARCEL', general: 'GENERAL LOGISTICS',
  };

  get logisticsInsightCards(): CompanyProfileInsight[] {
    const cards = this.logisticsInsightModules.flatMap(module =>
      (module.responses || []).flatMap(response => {
        const properties = response.display_schema?.properties;
        const entries = properties
          ? Object.entries(properties).map(([key, schema]) => ({ key, schema, value: response.answer?.[key] }))
          : [{ key: response.display_schema?.label || response.question || '', schema: response.display_schema, value: response.answer }];
        return entries.filter(entry => hasInsightValue(entry.value)).map(entry => {
          const categoryChart = buildInsightCategoryChart(entry.schema, entry.value);
          const section = this.logisticsInsightSection(entry.key + ' ' + ((entry.schema as any)?.label || ''));
          return {
            ...entry,
            categoryChart,
            displayType: categoryChart ? 'pie_chart' : (entry.schema as any)?.display_type,
            response,
            module: module.module,
            title: (entry.schema as any)?.label || entry.key.replace(/_/g, ' '),
            section,
            eyebrow: LogisticsCompanyProfileComponent.sectionEyebrows[section],
          };
        });
      })
    );
    const lensSections: Record<string, string[]> = {
      overview: ['parcel', 'freight', 'general', 'international', 'warehouse'],
      'freight-forwarding': ['freight', 'international', 'general', 'warehouse', 'parcel'],
      'third-party-logistics': ['warehouse', 'freight', 'general', 'parcel', 'international'],
      'parcel-courier': ['parcel', 'general', 'international', 'warehouse', 'freight'],
      'customs-trade': ['international', 'freight', 'general', 'parcel', 'warehouse'],
    };
    const order = lensSections[this.logisticsLens] || lensSections.overview;
    const isSalesCopy = (card: any) => /email_opener|identified_hooks/.test(card.key);
    // Lead with the answer formats that make the operational mix legible at a glance.
    // Keep the answer and its display schema together so the existing Magic Insights
    // chart/list components can render them without changing the evidence dialog.
    const visualPriority = (card: any): number => ({
      vertical_bar_chart: 0, pie_chart: 0, gauge: 0,
      card_list: 1, string_list: 1, labelled_pills: 2, pills: 2,
      number: 3, string: 4,
    })[card.displayType] ?? 4;
    if (this.logisticsLens === 'overview') {
      // Give the overview a mix of signals before any one category fills the page.
      const buckets = order.map(section => cards.filter(card => card.section === section && !isSalesCopy(card))
        .sort((a, b) => visualPriority(a) - visualPriority(b)));
      const overview = [];
      while (buckets.some(bucket => bucket.length)) {
        for (const bucket of buckets) {
          if (bucket.length) overview.push(bucket.shift());
        }
      }
      return [...overview, ...cards.filter(isSalesCopy)];
    }
    return cards.sort((a, b) =>
      Number(isSalesCopy(a)) - Number(isSalesCopy(b)) ||
      order.indexOf(a.section) - order.indexOf(b.section) ||
      visualPriority(a) - visualPriority(b)
    );
  }

  get visibleLogisticsInsightCards(): CompanyProfileInsight[] {
    const cards = this.logisticsInsightCards;
    return this.showAllLogisticsInsights ? cards : cards.slice(0, this.logisticsInitialCardCount);
  }

  get logisticsTradeActivity() {
    const source = this.company['imports_&_exports'];
    if (source !== this.tradeTimelineSource || !this.tradeTimelineCache) {
      this.tradeTimelineSource = source;
      this.tradeTimelineCache = buildTradeTimeline(source);
    }
    return this.tradeTimelineCache;
  }

  logisticsInsightSection(text: string): string {
    const value = text.toLowerCase();
    if (/email.opener|identified.hooks/.test(value)) return 'general';
    if (/warehouse|storage|fulfil|pallet|wms/.test(value)) return 'warehouse';
    if (/parcel|return|marketplace|b2c|ecommerce/.test(value)) return 'parcel';
    if (/country|countr|international|geograph|container|customs|cross.border/.test(value)) return 'international';
    if (/freight|transport|shipment|shipping|carrier|mode|vehicle/.test(value)) return 'freight';
    return 'general';
  }

  loadLogisticsProfileInsights(): void {
    if (!this.companyNumber) return;
    this.logisticsInsightsLoading = true;
    this.logisticsInsightsError = false;
    this.companyService.getLogisticsProfileInsights(this.companyNumber).subscribe({
      next: modules => {
        this.logisticsInsightModules = Array.isArray(modules) ? modules : [];
        this.logisticsInsightsLoading = false;
      },
      error: () => {
        this.logisticsInsightsError = true;
        this.logisticsInsightsLoading = false;
      },
    });
  }

  switchProfileMode(mode: 'full' | 'logistics'): void {
    this.authenticationService.setDefaultSkin(mode).subscribe({
      next: () => this.router.navigate(
        mode === 'logistics'
          ? ['/profile', this.companyNumber, 'logistics']
          : ['/profile', this.companyNumber],
        {
          queryParams: this.route.snapshot.queryParams,
          fragment: this.route.snapshot.fragment,
        }
      ),
      error: () => this.pubMethods.showInfoMessage('Could not save your profile preference. Please try again.'),
    });
  }
}
